import { useContext } from "react";
// @mui
import { useTheme } from "@mui/material/styles";
import { Box, Stack, AppBar, Toolbar, IconButton } from "@mui/material";
// utils
import { bgBlur } from "../../../../../minimals/utils/cssStyles";
// hooks
import useOffSetTop from "../../../../../minimals/hooks/useOffSetTop";
import useResponsive from "../../../../../minimals/hooks/useResponsive";
// config
import { HEADER, NAV } from "../../../../../minimals/config-global";
// components
import Logo from "../../../../../minimals/components/logo";
import Iconify from "../../../../../minimals/components/iconify";
import { useSettingsContext } from "../../../../../minimals/components/settings";
//
import { HeaderSlotContext } from "../HeaderSlotContext";
import Searchbar from "./Searchbar";
import AccountPopover from "./AccountPopover";
import LanguagePopover from "./LanguagePopover";
import ContactsPopover from "./ContactsPopover";
import NotificationsPopover from "./NotificationsPopover";

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();

  const { themeLayout } = useSettingsContext();

  const isNavHorizontal = themeLayout === "horizontal";

  const isNavMini = themeLayout === "mini";

  const isDesktop = useResponsive("up", "lg");

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const { setSlotEl } = useContext(HeaderSlotContext);

  const renderContent = (
    <>
      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: "text.primary" }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      {/* On mobile, pages can render into this slot (e.g. the /documents search bar). */}
      {!isDesktop && <Box ref={setSlotEl} sx={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center" }} />}

      <Stack flexGrow={isDesktop ? 1 : 0} direction="row" alignItems="center" justifyContent="flex-end" spacing={{ xs: 0.5, sm: 1.5 }}>

      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: "none",
        height: HEADER.H_MOBILE,
        // Desktop keeps this bar behind the content (the horizontal nav is the visible
        // header there). On mobile it hosts the hamburger and the page search slot, so
        // it has to sit above the content or those controls can't be clicked.
        zIndex: isDesktop ? -1 : theme.zIndex.appBar,
        // bgBlur relies on backdrop-filter, which iOS Safari drops on a position:fixed
        // element when its backdrop changes a lot (e.g. a results list collapsing to
        // "No results") — taking the whole header, and the search field in it, with it.
        // Mobile therefore uses a solid background; desktop keeps the blur (it sits
        // behind content there, so the bug never surfaces).
        ...(isDesktop
          ? bgBlur({ color: theme.palette.background.default })
          : {
              backgroundColor: theme.palette.background.default,
              // Force a stable GPU compositing layer so iOS Safari keeps the fixed
              // header painted through big backdrop changes / keyboard open+close.
              transform: "translateZ(0)",
              WebkitBackfaceVisibility: "hidden"
            }),
        transition: theme.transitions.create(["height"], {
          duration: theme.transitions.duration.shorter
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: "background.default",
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`
          })
        })
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 }
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
